/**
 * Profile creation wizard step registry.
 *
 * Steps are data, not code. Adding, reordering, or making a step optional is
 * a single-line change. Each step exposes a Zod schema validating just its
 * slice of the profile; the merged schema is the full profile.
 *
 * Step IDs are stable identifiers stored in the user's draft state — do not
 * rename without a migration.
 */

import { z } from 'zod';
import { ProfilePhotoSchema } from '../schemas/photo.js';
import { PreferencesSchema } from '../schemas/preferences.js';
import { ProfileSchema } from '../schemas/profile.js';

export type WizardStep = {
  /** Stable identifier. */
  id: string;
  /** i18n key for the step title (rendered at the UI layer). */
  titleKey: string;
  /** Whether the user can skip this step. */
  optional: boolean;
  /** Zod schema for this step's slice. */
  schema: z.ZodTypeAny;
};

// ─── Per-step schemas ─────────────────────────────────────────────────────

// Per-step schemas are exported so client forms can derive typed values
// from `z.infer<typeof BasicsStepSchema>` etc. Going via `getWizardStep`
// loses the inferred type because the registry's `schema` field is
// declared as `z.ZodTypeAny`.

export const PhoneVerificationStepSchema = z.object({
  phoneE164: z.string(),
  otpVerifiedAt: z.string().datetime({ offset: true }),
});

export const BasicsStepSchema = ProfileSchema.pick({
  displayName: true,
  dateOfBirth: true,
  gender: true,
  heightCm: true,
  maritalStatus: true,
  hasChildren: true,
  childrenCount: true,
  childrenCustody: true,
  nationalityIso: true,
  countryOfResidenceIso: true,
  ethnicities: true,
  languages: true,
  educationLevel: true,
  educationDiscipline: true,
  profession: true,
  industry: true,
  employmentStatus: true,
  motherTongue: true,
  grewUpCountryIso: true,
  grewUpRegion: true,
  // Marital-history depth (audit Spec J2). Conditionally rendered in
  // BasicsStep when maritalStatus !== 'never_married' — captures
  // "years since" + custody nuance + the user's seeking pace +
  // the openAboutPast opt-in that gates display on the public profile.
  // All optional + nullable; never-married users skip these silently.
  yearsSinceMaritalChange: true,
  childrenLivingSituation: true,
  seekingPace: true,
  openAboutPast: true,
  // Location is captured during the basics step (browser geolocation API
  // on web, expo-location on mobile). It's a profile field, not its own
  // wizard step — single-screen capture flow.
  home: true,
  homeCity: true,
});

export const ReligionStepSchema = ProfileSchema.pick({
  sect: true,
  subSect: true,
  religiosity: true,
  prays: true,
  fastsRamadan: true,
  quranEngagement: true,
  dressStyle: true,
  diet: true,
  alcohol: true,
  smoking: true,
  convertStatus: true,
});

// Islamic-practice depth (matrimonial-polish audit PR #2). Optional
// tile that captures Hajj/Umrah completion, hifz status, and mosque
// attendance — the kind of signals that make the profile read
// matrimonial rather than dating-derived. Every field defaults to
// `prefer_not_to_say` so a user who doesn't want to disclose just
// taps Next.
export const IslamicPracticeStepSchema = ProfileSchema.pick({
  hajjStatus: true,
  umrahStatus: true,
  quranMemorization: true,
  mosqueAttendance: true,
});

export const MarriageIntentStepSchema = ProfileSchema.pick({
  marriageTimeframe: true,
  familyInvolvement: true,
  wantsChildren: true,
  relocation: true,
  livingArrangement: true,
  polygamyPreference: true,
});

export const AboutStepSchema = ProfileSchema.pick({
  bio: true,
  interests: true,
  personalityTraits: true,
  promptAnswers: true,
});

// Audit Spec C2 + C3 + C4 — financial readiness, mahr expectation,
// and family-background fields. All optional disclosures, all default
// to `prefer_not_to_say`. The wizard tile sits between marriage_intent
// and about — it's the natural place because these are the
// "background" the user is bringing into the marriage, not their
// religious practice or marriage-shape preferences.
//
// Mutual disclosure is enforced server-side in the public-profile
// route (profile-service/src/routes/public-profile.ts): a viewer
// only sees a candidate's real value when they've disclosed the same
// field themselves. The tile is optional so users who don't want to
// disclose any of these can skip without blocking finalize.
export const BackgroundStepSchema = ProfileSchema.pick({
  incomeBracket: true,
  currentlySupportsFamily: true,
  financiallyIndependent: true,
  mahrExpectation: true,
  familyPractice: true,
  familyEducationLevel: true,
  familyResidence: true,
});

// Partner preferences carries the `paidApproachOnly` toggle. It's a
// Profile field, not a Preferences field, but it lives on this step
// because "do they have to pay to approach me?" is naturally a
// matching-rules question. mergeAll routes the value onto the
// Profile row at finalize time.
export const PartnerPreferencesStepSchema = PreferencesSchema.and(
  z.object({ paidApproachOnly: z.boolean().default(false) }),
);

// Photos carries the `blurUntilMatch` toggle. It belongs alongside the
// photos themselves — that's where the user thinks about who sees
// their face — rather than tucked away in a separate Privacy tile.
export const PhotosStepSchema = z.object({
  photos: z.array(ProfilePhotoSchema).min(1).max(3),
  blurUntilMatch: z.boolean().default(false),
});

export const LivenessStepSchema = z.object({
  livenessSessionId: z.string(),
  livenessVerifiedAt: z.string().datetime({ offset: true }),
});

// Optional wali step. Lives at the end of the wizard — users can skip
// during signup and add a wali from Settings later. Plan §Family /
// Wali Involvement §MVP.
//
// All fields are optional+nullable so a user can skip the entire step
// (which sends `{}` server-side and persists nothing). When they DO
// fill it in, the email + name are typically populated together; phone
// is optional.
export const WaliStepSchema = ProfileSchema.pick({
  waliName: true,
  waliRelationship: true,
  waliEmail: true,
  waliPhoneE164: true,
  // Audit Spec F5 — optional; lets the chat-header overlap indicator
  // surface "Both walis speak X" when the partner has also disclosed.
  waliLanguage: true,
});

// ─── Step registry ────────────────────────────────────────────────────────

// Order is what the UI renders in. The single-page wizard / profile-edit
// shows every tile in this order from top to bottom. "About you" leads —
// it sets the tone of the conversation. Phone verification sits at the
// bottom so users who already verified (e.g. via mobile signup) don't
// re-see it, and new users hit it as the last gate before finalize.
// Liveness sits between photos and phone for the same "verify after
// filling in" reason.
export const WIZARD_STEPS: readonly WizardStep[] = [
  { id: 'basics', titleKey: 'wizard.step.basics', optional: false, schema: BasicsStepSchema },
  { id: 'religion', titleKey: 'wizard.step.religion', optional: false, schema: ReligionStepSchema },
  // Islamic-practice depth — optional. Sits between religion and
  // marriage_intent because it deepens the religious-practice picture
  // (Hajj/Umrah/hifz/mosque) without being part of "where do we want
  // to live + when do we tell family." Skippable: every field defaults
  // to `prefer_not_to_say`.
  {
    id: 'islamic_practice',
    titleKey: 'wizard.step.islamic_practice',
    optional: true,
    schema: IslamicPracticeStepSchema,
  },
  {
    id: 'marriage_intent',
    titleKey: 'wizard.step.marriage_intent',
    optional: false,
    schema: MarriageIntentStepSchema,
  },
  // Background — optional. Income / mahr / family disclosure is
  // sensitive (especially for women in some markets), so this stays
  // skippable. Mutual-disclosure rule in the public-profile route
  // means undisclosed values are invisible to viewers regardless of
  // whether the user filled the tile.
  {
    id: 'background',
    titleKey: 'wizard.step.background',
    optional: true,
    schema: BackgroundStepSchema,
  },
  { id: 'about', titleKey: 'wizard.step.about', optional: true, schema: AboutStepSchema },
  // Wali — optional. Users who skip add a wali later from Settings.
  { id: 'wali', titleKey: 'wizard.step.wali', optional: true, schema: WaliStepSchema },
  {
    id: 'partner_preferences',
    titleKey: 'wizard.step.partner_preferences',
    optional: false,
    schema: PartnerPreferencesStepSchema,
  },
  { id: 'photos', titleKey: 'wizard.step.photos', optional: false, schema: PhotosStepSchema },
  // Liveness — optional at signup. Users can finalize without
  // completing the selfie-liveness check; profiles without it show
  // no "Verified" badge to other users (vs. an explicit "Liveness
  // verified" badge once they pass). They can run the check later
  // from Settings → Verify identity. This trades a hard signup gate
  // for higher conversion + voluntary verification post-signup, with
  // the social signal (badge) doing the work the gate used to do.
  { id: 'liveness', titleKey: 'wizard.step.liveness', optional: true, schema: LivenessStepSchema },
  // Phone last — skipped at render time when already verified.
  {
    id: 'phone_verification',
    titleKey: 'wizard.step.phone',
    optional: false,
    schema: PhoneVerificationStepSchema,
  },
] as const;

export type WizardStepId = (typeof WIZARD_STEPS)[number]['id'];

/** Look up a step by id. */
export function getWizardStep(id: WizardStepId): WizardStep {
  const step = WIZARD_STEPS.find((s) => s.id === id);
  if (!step) {
    throw new Error(`Unknown wizard step: ${id}`);
  }
  return step;
}
